import { Alert, Collapse, Grid } from '@mui/material'
import { useNewUOMFormContext } from '../hooks/useNewUOMForm'
import OWInput from 'components/OWForm/OWInput'
import { useTranslation } from 'react-i18next'
import OWSelect, { TOption } from 'components/OWForm/OWSelect'
import { UOMContract } from 'models'
import { useCallback, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import OWSwitch from 'components/OWForm/OWSwitch'
import UOMConversionForm from './UOMConversionForm'
import { NO_REF_UOM_ID } from '../hooks/useGetUOMAndDependencies'
import { isEmpty } from 'lodash-es'
import HelperTooltip from 'components/Tooltip/HelperTooltip'

function NewUOMFormContainer({
    disabled,
    uomClasses,
    uoms,
}: {
    disabled: boolean
    uomClasses: TOption[]
    uoms: UOMContract[]
}) {
    const { control, setValue } = useNewUOMFormContext()
    const { t } = useTranslation()

    const [
        enableConversion,
        uomClassID,
        isConversionModifiable,
        uomID,
        uomName,
    ] = useWatch({
        control: control,
        name: [
            'enableConversion',
            'uomClassID',
            'isConversionModifiable',
            'uomid',
            'name',
        ],
    })
    const hasName = useMemo(() => !isEmpty(uomName?.trim() ?? ''), [uomName])
    const name = useMemo(
        () => (hasName ? uomName! : `<${t('uoms.label.uom_name')}>`),
        [hasName, uomName, t],
    )

    const usableUOMs = useMemo(
        () => [
            { value: NO_REF_UOM_ID, label: name + ' (Current UOM)' } as TOption,
            ...(
                uoms.filter(
                    (i) => i.uomClassID === uomClassID && i.uomid !== uomID,
                ) ?? []
            ).map((uom) => {
                return { value: uom.uomid, label: uom.name! } as TOption
            }),
        ],
        [uomClassID, uoms, name, uomID],
    )

    const onChangeUOMClass = useCallback(() => {
        setValue('referenceUOMID', NO_REF_UOM_ID)
        setValue('conversion', 1)
    }, [setValue])

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <OWInput
                    control={control}
                    name="name"
                    fullWidth
                    label={t('uoms.label.uom_name')}
                    disabled={disabled}
                    inputProps={{ maxLength: 30 }}
                />
            </Grid>
            <Grid item xs={12}>
                <OWInput
                    control={control}
                    name="symbol"
                    fullWidth
                    label={t('uoms.label.symbol')}
                    disabled={disabled}
                    inputProps={{ maxLength: 10 }}
                />
            </Grid>
            <Grid item xs={12}>
                <OWInput
                    control={control}
                    name="description"
                    fullWidth
                    label={
                        t('uoms.label.description') +
                        ' ' +
                        t('common.label.optional')
                    }
                    disabled={disabled}
                    inputProps={{ maxLength: 250 }}
                    multiline
                    rows={5}
                />
            </Grid>
            {!isConversionModifiable && (
                <Grid item xs={12}>
                    <Alert color="info" severity="info">
                        {t('uoms.message.conversion_not_modifiable')}
                    </Alert>
                </Grid>
            )}
            <Grid item xs={12}>
                <OWSelect
                    control={control}
                    name="uomClassID"
                    label={t('uoms.label.uom_category')}
                    options={uomClasses}
                    disabled={disabled || !isConversionModifiable}
                    onChangeCallback={onChangeUOMClass}
                    fullWidth
                />
            </Grid>
            <Grid item xs={12}>
                <HelperTooltip
                    helperText={t('uoms.message.tooltip.enable_conversion')}
                >
                    <OWSwitch
                        control={control}
                        name="enableConversion"
                        label={t('uoms.label.conversion')}
                        disabled={disabled || !isConversionModifiable}
                    />
                </HelperTooltip>
            </Grid>
            <Grid item xs={12}>
                <Collapse in={enableConversion}>
                    <UOMConversionForm
                        uoms={usableUOMs}
                        hasName={hasName}
                        currentName={name}
                        disabled={disabled || !isConversionModifiable}
                    />
                </Collapse>
            </Grid>
        </Grid>
    )
}

export default NewUOMFormContainer
